import React, { useState, Component } from 'react';
import { ChevronLeft, ChevronRight, AlertTriangle, Check, Calendar as CalendarIcon, X, User, Mail, Phone, MapPin, Users, CreditCard, Home, ExternalLink, GripVertical, Lock, Plus } from 'lucide-react';
import { MobileLayout } from '../components/MobileLayout';
// Types for the Gantt Chart
type EventType = 'checkout' | 'checkin' | 'cleaning-suggested' | 'cleaning-confirmed' | 'external';
interface CalendarEvent {
  id: string;
  type: EventType;
  timeStart: string;
  timeEnd?: string;
  label: string;
  details?: string;
  source?: 'google' | 'outlook'; // For external events
}
interface DaySchedule {
  date: string;
  dayName: string;
  dayNumber: string;
  events: CalendarEvent[];
  isToday?: boolean;
}
export function CalendarioPage() {
  const [viewMode, setViewMode] = useState<'week' | 'list'>(() => {
    if (typeof window !== 'undefined' && window.innerWidth < 1024) {
      return 'list';
    }
    return 'week';
  });
  const [isBookingModalOpen, setIsBookingModalOpen] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState<CalendarEvent | null>(null);
  const [isGoogleModalOpen, setIsGoogleModalOpen] = useState(false);
  const [isOutlookModalOpen, setIsOutlookModalOpen] = useState(false);
  // Drag & Resize state
  const [draggedEvent, setDraggedEvent] = useState<string | null>(null);
  const [resizingEvent, setResizingEvent] = useState<string | null>(null);
  const [resizeStartY, setResizeStartY] = useState<number>(0);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [confirmationMessage, setConfirmationMessage] = useState('');
  const [showConflictWarning, setShowConflictWarning] = useState(false);
  const [conflictMessage, setConflictMessage] = useState('');
  // Integration states
  const [integrations, setIntegrations] = useState({
    google: true,
    outlook: false
  });
  // Mock Google accounts with calendar selection
  const [googleAccounts, setGoogleAccounts] = useState([{
    id: '1',
    email: 'mario.rossi@gmail.com',
    calendars: [{
      id: 'cal1',
      name: 'Personale',
      selected: true
    }, {
      id: 'cal2',
      name: 'Lavoro',
      selected: true
    }, {
      id: 'cal3',
      name: 'Villa Sunset',
      selected: false
    }, {
      id: 'cal4',
      name: 'Famiglia',
      selected: false
    }]
  }, {
    id: '2',
    email: 'mario.rossi.work@gmail.com',
    calendars: [{
      id: 'cal5',
      name: 'Riunioni',
      selected: true
    }, {
      id: 'cal6',
      name: 'Progetti',
      selected: false
    }]
  }]);
  // Mock Outlook accounts with calendar selection
  const [outlookAccounts, setOutlookAccounts] = useState([{
    id: '1',
    email: 'mario.rossi@outlook.com',
    calendars: [{
      id: 'cal1',
      name: 'Calendario',
      selected: true
    }, {
      id: 'cal2',
      name: 'Compleanni',
      selected: false
    }]
  }]);
  // Mock Data - Now as state to make it mutable - EXTENDED TO 14 DAYS WITH MORE EVENTS
  const [weekData, setWeekData] = useState<DaySchedule[]>([{
    date: '2024-01-15',
    dayName: 'Lun',
    dayNumber: '15',
    events: [{
      id: '16',
      type: 'checkout',
      timeStart: '10:00',
      label: 'Roberto F.'
    }, {
      id: '17',
      type: 'cleaning-suggested',
      timeStart: '11:00',
      timeEnd: '15:00',
      label: 'Pulizia'
    }, {
      id: '18',
      type: 'checkin',
      timeStart: '15:00',
      label: 'Francesca N.'
    }]
  }, {
    date: '2024-01-16',
    dayName: 'Mar',
    dayNumber: '16',
    events: [{
      id: '19',
      type: 'checkout',
      timeStart: '11:00',
      label: 'Luca P.'
    }, {
      id: '20',
      type: 'cleaning-suggested',
      timeStart: '12:00',
      timeEnd: '16:00',
      label: 'Pulizia'
    }, {
      id: '21',
      type: 'checkin',
      timeStart: '16:00',
      label: 'Giulia S.'
    }]
  }, {
    date: '2024-01-17',
    dayName: 'Mer',
    dayNumber: '17',
    events: [{
      id: 'ext-1',
      type: 'external',
      timeStart: '09:00',
      timeEnd: '10:00',
      label: 'Team meeting',
      source: 'google'
    }]
  }, {
    date: '2024-01-18',
    dayName: 'Gio',
    dayNumber: '18',
    events: [{
      id: '22',
      type: 'checkout',
      timeStart: '10:00',
      label: 'Francesca N.'
    }, {
      id: '23',
      type: 'cleaning-suggested',
      timeStart: '11:00',
      timeEnd: '14:00',
      label: 'Pulizia'
    }, {
      id: '24',
      type: 'checkin',
      timeStart: '14:00',
      label: 'Davide C.'
    }]
  }, {
    date: '2024-01-19',
    dayName: 'Ven',
    dayNumber: '19',
    events: [{
      id: '4',
      type: 'checkout',
      timeStart: '11:00',
      label: 'Sofia L.'
    }, {
      id: '5',
      type: 'cleaning-suggested',
      timeStart: '12:00',
      timeEnd: '14:30',
      label: 'Pulizia (Stretto!)'
    }, {
      id: '6',
      type: 'checkin',
      timeStart: '14:30',
      label: 'Paolo V.'
    }, {
      id: 'ext-2',
      type: 'external',
      timeStart: '16:00',
      timeEnd: '17:00',
      label: 'Call cliente',
      source: 'outlook'
    }]
  }, {
    date: '2024-01-20',
    dayName: 'Sab',
    dayNumber: '20',
    events: []
  }, {
    date: '2024-01-21',
    dayName: 'Dom',
    dayNumber: '21',
    events: []
  }, {
    date: '2024-01-22',
    dayName: 'Lun',
    dayNumber: '22',
    events: [{
      id: '28',
      type: 'checkout',
      timeStart: '11:00',
      label: 'Paolo V.'
    }, {
      id: '29',
      type: 'cleaning-suggested',
      timeStart: '12:00',
      timeEnd: '15:00',
      label: 'Pulizia'
    }, {
      id: '30',
      type: 'checkin',
      timeStart: '15:00',
      label: 'Chiara B.'
    }]
  }, {
    date: '2024-01-23',
    dayName: 'Mar',
    dayNumber: '23',
    events: [{
      id: '31',
      type: 'checkout',
      timeStart: '10:00',
      label: 'Davide C.'
    }, {
      id: '32',
      type: 'cleaning-suggested',
      timeStart: '11:00',
      timeEnd: '16:00',
      label: 'Pulizia'
    }, {
      id: '33',
      type: 'checkin',
      timeStart: '16:00',
      label: 'Valentina R.'
    }]
  }, {
    date: '2024-01-24',
    dayName: 'Mer',
    dayNumber: '24',
    events: []
  }, {
    date: '2024-01-25',
    dayName: 'Gio',
    dayNumber: '25',
    events: [{
      id: '34',
      type: 'checkout',
      timeStart: '11:00',
      label: 'Elena T.'
    }, {
      id: '35',
      type: 'cleaning-suggested',
      timeStart: '12:00',
      timeEnd: '14:30',
      label: 'Pulizia (Stretto!)'
    }, {
      id: '36',
      type: 'checkin',
      timeStart: '14:30',
      label: 'Simone G.'
    }]
  }, {
    date: '2024-01-26',
    dayName: 'Ven',
    dayNumber: '26',
    events: [{
      id: '37',
      type: 'checkout',
      timeStart: '10:00',
      label: 'Andrea M.'
    }, {
      id: '38',
      type: 'cleaning-suggested',
      timeStart: '11:00',
      timeEnd: '15:00',
      label: 'Pulizia'
    }, {
      id: '39',
      type: 'checkin',
      timeStart: '15:00',
      label: 'Martina L.'
    }]
  }, {
    date: '2024-01-27',
    dayName: 'Sab',
    dayNumber: '27',
    events: [{
      id: '13',
      type: 'checkout',
      timeStart: '11:00',
      label: 'Laura M.'
    }, {
      id: '14',
      type: 'cleaning-suggested',
      timeStart: '12:00',
      timeEnd: '16:00',
      label: 'Pulizia'
    }, {
      id: '15',
      type: 'checkin',
      timeStart: '16:00',
      label: 'Giuseppe P.'
    }]
  }, {
    date: '2024-01-28',
    dayName: 'Dom',
    dayNumber: '28',
    events: [{
      id: '40',
      type: 'checkout',
      timeStart: '11:00',
      label: 'Chiara B.'
    }, {
      id: '41',
      type: 'cleaning-suggested',
      timeStart: '12:00',
      timeEnd: '16:00',
      label: 'Pulizia'
    }, {
      id: '42',
      type: 'checkin',
      timeStart: '16:00',
      label: 'Federico T.'
    }]
  }]);
  // Helper: Convert minutes to time string
  const minutesToTime = (minutes: number): string => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}`;
  };
  // Helper: Convert time string to minutes
  const timeToMinutes = (time: string): number => {
    const [hours, mins] = time.split(':').map(Number);
    return hours * 60 + mins;
  };
  // Helper: Snap to 30-minute intervals
  const snapTo30Minutes = (minutes: number): number => {
    return Math.round(minutes / 30) * 30;
  };
  // Helper: Check for conflicts with fixed events
  const hasConflict = (dayIndex: number, startTime: string, endTime: string, excludeEventId: string): boolean => {
    const startMinutes = timeToMinutes(startTime);
    const endMinutes = timeToMinutes(endTime);
    const dayEvents = weekData[dayIndex].events.filter(e => e.id !== excludeEventId);
    for (const event of dayEvents) {
      if (event.type === 'checkin' || event.type === 'checkout') {
        const eventMinutes = timeToMinutes(event.timeStart);
        if (event.type === 'checkout') {
          // Cleaning must START at or after checkout time
          if (startMinutes < eventMinutes) {
            return true; // Conflict: cleaning starts before checkout
          }
        }
        if (event.type === 'checkin') {
          // Cleaning must END at or before checkin time
          if (endMinutes > eventMinutes) {
            return true; // Conflict: cleaning ends after checkin
          }
        }
      }
    }
    return false;
  };
  // Drag handlers
  const handleDragStart = (e: React.DragEvent, eventId: string, dayIndex: number) => {
    setDraggedEvent(eventId);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('eventId', eventId);
    e.dataTransfer.setData('dayIndex', dayIndex.toString());
  };
  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };
  const handleDrop = (e: React.DragEvent, targetDayIndex: number) => {
    e.preventDefault();
    const eventId = e.dataTransfer.getData('eventId');
    const sourceDayIndex = parseInt(e.dataTransfer.getData('dayIndex'));
    if (!eventId) return;
    // Calculate new time based on drop position
    const rect = e.currentTarget.getBoundingClientRect();
    const y = e.clientY - rect.top;
    const totalHeight = rect.height;
    const minutesFromStart = y / totalHeight * 720; // 720 minutes = 12 hours (8:00-20:00)
    const snappedMinutes = snapTo30Minutes(minutesFromStart + 480); // 480 = 8:00 AM
    let newStartTime = minutesToTime(snappedMinutes);
    // Find the event
    let event: CalendarEvent | undefined;
    let duration = 60; // default 1 hour
    for (const day of weekData) {
      const found = day.events.find(e => e.id === eventId);
      if (found) {
        event = found;
        if (found.timeEnd) {
          duration = timeToMinutes(found.timeEnd) - timeToMinutes(found.timeStart);
        }
        break;
      }
    }
    if (!event) return;
    let newEndTime = minutesToTime(snappedMinutes + duration);
    // Find checkout and checkin times for this day
    const dayEvents = weekData[targetDayIndex].events;
    const checkout = dayEvents.find(e => e.type === 'checkout');
    const checkin = dayEvents.find(e => e.type === 'checkin');
    let adjustedStartTime = newStartTime;
    let adjustedEndTime = newEndTime;
    // If there's a checkout, ensure we start at or after it
    if (checkout) {
      const checkoutMinutes = timeToMinutes(checkout.timeStart);
      const startMinutes = timeToMinutes(newStartTime);
      if (startMinutes < checkoutMinutes) {
        // Snap to checkout time
        adjustedStartTime = checkout.timeStart;
        adjustedEndTime = minutesToTime(checkoutMinutes + duration);
      }
    }
    // If there's a checkin, ensure we end at or before it
    if (checkin) {
      const checkinMinutes = timeToMinutes(checkin.timeStart);
      const endMinutes = timeToMinutes(adjustedEndTime);
      if (endMinutes > checkinMinutes) {
        // Snap end to checkin time
        adjustedEndTime = checkin.timeStart;
        // Adjust start if duration doesn't fit
        const newStartMinutes = checkinMinutes - duration;
        if (checkout && newStartMinutes < timeToMinutes(checkout.timeStart)) {
          // Not enough space - show error
          setConflictMessage("Non c'è abbastanza spazio per questo intervento");
          setShowConflictWarning(true);
          setTimeout(() => setShowConflictWarning(false), 3000);
          setDraggedEvent(null);
          return;
        }
        adjustedStartTime = minutesToTime(Math.max(480, newStartMinutes)); // 480 = 8:00
      }
    }
    // Use adjusted times for conflict check
    if (hasConflict(targetDayIndex, adjustedStartTime, adjustedEndTime, eventId)) {
      setConflictMessage('Impossibile spostare qui: sovrapposizione con check-in/check-out');
      setShowConflictWarning(true);
      setTimeout(() => setShowConflictWarning(false), 3000);
      setDraggedEvent(null);
      return;
    }
    // Update event position with adjusted times
    setWeekData(prevData => {
      const newData = [...prevData];
      // Remove from source day
      newData[sourceDayIndex] = {
        ...newData[sourceDayIndex],
        events: newData[sourceDayIndex].events.filter(e => e.id !== eventId)
      };
      // Add to target day with adjusted time
      const updatedEvent = {
        ...event!,
        timeStart: adjustedStartTime,
        timeEnd: adjustedEndTime
      };
      newData[targetDayIndex] = {
        ...newData[targetDayIndex],
        events: [...newData[targetDayIndex].events, updatedEvent]
      };
      return newData;
    });
    setConfirmationMessage(`Intervento spostato alle ${adjustedStartTime}`);
    setShowConfirmation(true);
    setTimeout(() => setShowConfirmation(false), 3000);
    setDraggedEvent(null);
  };
  // Resize handlers - FIXED VERSION
  const handleResizeStart = (e: React.MouseEvent, eventId: string) => {
    e.preventDefault();
    e.stopPropagation();
    let currentY = e.clientY;
    let isResizing = true;
    const handleMouseMove = (moveEvent: MouseEvent) => {
      if (!isResizing) return;
      const deltaY = moveEvent.clientY - currentY;
      const minutesChange = Math.round(deltaY / 48 * 60); // 48px per hour
      const snappedChange = snapTo30Minutes(minutesChange);
      if (snappedChange === 0) return;
      setWeekData(prevData => {
        const newData = [...prevData];
        for (let dayIndex = 0; dayIndex < newData.length; dayIndex++) {
          const eventIndex = newData[dayIndex].events.findIndex(e => e.id === eventId);
          if (eventIndex !== -1) {
            const event = newData[dayIndex].events[eventIndex];
            if (!event.timeEnd) return prevData;
            const currentEndMinutes = timeToMinutes(event.timeEnd);
            const newEndMinutes = currentEndMinutes + snappedChange;
            const startMinutes = timeToMinutes(event.timeStart);
            // Minimum 30 minutes duration
            if (newEndMinutes - startMinutes < 30) return prevData;
            // Maximum 20:00 (1200 minutes)
            if (newEndMinutes > 1200) return prevData;
            const newEndTime = minutesToTime(newEndMinutes);
            // Check conflicts with checkin
            const checkin = newData[dayIndex].events.find(e => e.type === 'checkin');
            if (checkin && newEndMinutes > timeToMinutes(checkin.timeStart)) {
              return prevData;
            }
            newData[dayIndex] = {
              ...newData[dayIndex],
              events: newData[dayIndex].events.map(e => e.id === eventId ? {
                ...e,
                timeEnd: newEndTime
              } : e)
            };
            // Update currentY for next move
            currentY = moveEvent.clientY;
            break;
          }
        }
        return newData;
      });
    };
    const handleMouseUp = () => {
      isResizing = false;
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      setConfirmationMessage('Durata intervento modificata');
      setShowConfirmation(true);
      setTimeout(() => setShowConfirmation(false), 3000);
    };
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };
  const toggleGoogleCalendar = (accountId: string, calendarId: string) => {
    setGoogleAccounts(googleAccounts.map(account => account.id === accountId ? {
      ...account,
      calendars: account.calendars.map(cal => cal.id === calendarId ? {
        ...cal,
        selected: !cal.selected
      } : cal)
    } : account));
  };
  const toggleOutlookCalendar = (accountId: string, calendarId: string) => {
    setOutlookAccounts(outlookAccounts.map(account => account.id === accountId ? {
      ...account,
      calendars: account.calendars.map(cal => cal.id === calendarId ? {
        ...cal,
        selected: !cal.selected
      } : cal)
    } : account));
  };
  // Helper to calculate vertical position based on day and horizontal position based on time
  const getPositionStyle = (dayIndex: number, timeStart: string, timeEnd?: string) => {
    const startHour = parseInt(timeStart.split(':')[0]);
    const startMin = parseInt(timeStart.split(':')[1]);
    const startTotalMinutes = (startHour - 8) * 60 + startMin;
    const topPercent = startTotalMinutes / 720 * 100;
    const leftPercent = dayIndex / 14 * 100; // Changed from 7 to 14
    const widthPercent = 100 / 14; // Changed from 7 to 14
    let heightPercent = 0;
    if (timeEnd) {
      const endHour = parseInt(timeEnd.split(':')[0]);
      const endMin = parseInt(timeEnd.split(':')[1]);
      const endTotalMinutes = (endHour - 8) * 60 + endMin;
      const durationMinutes = endTotalMinutes - startTotalMinutes;
      heightPercent = durationMinutes / 720 * 100;
    } else {
      heightPercent = 60 / 720 * 100;
    }
    return {
      left: `${Math.max(0, Math.min(100, leftPercent))}%`,
      width: `${Math.max(0, Math.min(100, widthPercent))}%`,
      top: `${Math.max(0, Math.min(100, topPercent))}%`,
      height: `${Math.max(0, Math.min(100, heightPercent))}%`
    };
  };
  const handleEventClick = (event: CalendarEvent) => {
    if (event.type === 'checkin' || event.type === 'checkout') {
      setSelectedBooking(event);
      setIsBookingModalOpen(true);
    }
  };
  const hours = Array.from({
    length: 13
  }, (_, i) => i + 8);
  return <MobileLayout data-id="element-892">
      {/* Page Header - Updated with Integrations */}
      <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4 mb-6 lg:mb-8" data-id="element-893">
        <div data-id="element-894">
          <h1 className="text-xl lg:text-2xl font-bold text-navy" data-id="element-895">
            Pianificazione
          </h1>
          <p className="text-xs lg:text-sm text-gray-500 mt-1" data-id="element-896">
            Gestisci gli interventi di pulizia
          </p>
        </div>

        {/* Calendar Integrations */}
        <div className="flex flex-col items-start lg:items-end gap-2" data-id="element-897">
          <span className="text-xs text-gray-400 uppercase tracking-wider font-medium" data-id="element-898">
            Integrazioni
          </span>
          <div className="flex items-center gap-2" data-id="element-899">
            {/* Google Calendar Badge */}
            <button onClick={() => setIsGoogleModalOpen(true)} className={`relative flex items-center justify-center p-2 bg-white border rounded-lg transition-all ${integrations.google ? 'border-gray-200 hover:border-gray-300' : 'border-gray-200 hover:border-gray-300 opacity-40'}`} title={integrations.google ? '✓ Connesso a Google Calendar' : 'Clicca per connettere Google Calendar'} data-id="element-900">
              <img src="https://cdn.magicpatterns.com/uploads/kH8QC7bV2biHRqFW7LmspQ/Google_Calendar_icon.png" alt="Google Calendar" className="w-5 h-5" data-id="element-901" />
              <span className={`absolute -top-1 -right-1 w-3 h-3 rounded-full border-2 border-white ${integrations.google ? 'bg-green-500' : 'bg-gray-400'}`} data-id="element-902"></span>
            </button>

            {/* Outlook Badge */}
            <button onClick={() => setIsOutlookModalOpen(true)} className={`relative flex items-center justify-center p-2 bg-white border rounded-lg transition-all ${integrations.outlook ? 'border-gray-200 hover:border-gray-300' : 'border-gray-200 hover:border-gray-300 opacity-40'}`} title={integrations.outlook ? '✓ Connesso a Outlook' : 'Clicca per connettere Outlook'} data-id="element-903">
              <img src="https://cdn.magicpatterns.com/uploads/mD3xBbUgdyBxcWhKYbGEK6/Microsoft_Office_Outlook.png" alt="Outlook" className="w-5 h-5" data-id="element-904" />
              <span className={`absolute -top-1 -right-1 w-3 h-3 rounded-full border-2 border-white ${integrations.outlook ? 'bg-green-500' : 'bg-gray-400'}`} data-id="element-905"></span>
            </button>
          </div>
        </div>
      </div>

      {/* Week Navigation */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4 lg:mb-6" data-id="element-906">
        <div className="flex items-center gap-2 lg:gap-4" data-id="element-907">
          <div className="flex items-center gap-1 lg:gap-2" data-id="element-908">
            <button className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors text-gray-500 min-h-[44px] min-w-[44px] flex items-center justify-center" data-id="element-909">
              <ChevronLeft size={20} data-id="element-910" />
            </button>
            <span className="text-sm font-bold text-navy whitespace-nowrap" data-id="element-911">
              15-28 Gen 2024
            </span>
            <button className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors text-gray-500 min-h-[44px] min-w-[44px] flex items-center justify-center" data-id="element-912">
              <ChevronRight size={20} data-id="element-913" />
            </button>
          </div>
          <button className="px-3 py-2 border border-gray-200 rounded-lg text-sm font-medium text-gray-600 hover:bg-gray-50 transition-colors min-h-[44px]" data-id="element-914">
            Oggi
          </button>
        </div>

        {/* View Toggle - Styled like Abbonamenti tabs */}
        <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-1" data-id="element-915">
          <button onClick={() => setViewMode('week')} className={`hidden lg:block flex-1 sm:flex-none px-4 py-2 rounded-md text-sm font-medium transition-all min-h-[44px] ${viewMode === 'week' ? 'bg-white text-navy shadow-sm' : 'text-gray-500 hover:text-navy'}`} data-id="element-916">
            Settimana
          </button>
          <button onClick={() => setViewMode('list')} className={`flex-1 sm:flex-none px-4 py-2 rounded-md text-sm font-medium transition-all min-h-[44px] ${viewMode === 'list' ? 'bg-white text-navy shadow-sm' : 'text-gray-500 hover:text-navy'}`} data-id="element-917">
            Lista
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex gap-4" data-id="element-918">
        <div className="flex-1 min-w-0" data-id="element-919">
          {/* Gantt Chart Card - With drag & drop enabled */}
          {viewMode === 'week' && <div className="bg-white rounded-xl border border-gray-200 overflow-hidden" data-id="element-920">
              <div className="overflow-x-auto" data-id="element-921">
                <div className="min-w-[1800px]" data-id="element-922">
                  {/* Days Header Row */}
                  <div className="flex border-b border-gray-200" data-id="element-923">
                    <div className="w-20 flex-shrink-0 border-r border-gray-200 bg-gray-50 sticky left-0 z-20" data-id="element-924"></div>
                    <div className="flex-1 flex bg-gray-50/50" data-id="element-925">
                      {weekData.map(day => <div key={day.date} className="flex-1 h-10 border-l border-gray-200 flex items-center justify-center min-w-[120px]" data-id="element-926">
                          <div className="flex items-center gap-2" data-id="element-927">
                            <span className="text-xs text-gray-400 font-medium" data-id="element-928">
                              {day.dayNumber}
                            </span>
                            <span className="text-xs text-gray-400 font-medium" data-id="element-929">
                              {day.dayName}
                            </span>
                          </div>
                        </div>)}
                    </div>
                  </div>

                  {/* Hours Rows with Events Container */}
                  <div className="relative" data-id="element-930">
                    {/* Hours Rows */}
                    <div className="divide-y divide-gray-200 border-b border-gray-200" data-id="element-931">
                      {hours.map(hour => <div key={hour} className="flex h-12 group hover:bg-gray-50/30 transition-colors" data-id="element-932">
                          <div className="w-20 flex-shrink-0 border-r border-gray-200 flex items-center justify-center bg-gray-50 sticky left-0 z-20" data-id="element-933">
                            <span className="text-xs text-gray-400 font-medium" data-id="element-934">
                              {hour}:00
                            </span>
                          </div>
                          <div className="flex-1 relative" data-id="element-935">
                            {weekData.map((_, dayIndex) => <div key={dayIndex} className="absolute top-0 bottom-0 border-l border-gray-200" style={{
                        left: `${dayIndex / 14 * 100}%`
                      }} data-id="element-936" />)}
                          </div>
                        </div>)}
                    </div>

                    {/* Events Overlay - With drag & drop */}
                    <div className="absolute top-0 left-0 right-0 bottom-0 pointer-events-none" data-id="element-937">
                      <div className="ml-20 h-full relative pointer-events-auto" data-id="element-938">
                        {weekData.map((day, dayIndex) => <div key={day.date} className="absolute top-0 bottom-0" style={{
                      left: `${dayIndex / 14 * 100}%`,
                      width: `${100 / 14}%`
                    }} onDragOver={handleDragOver} onDrop={e => handleDrop(e, dayIndex)} data-id="element-939">
                            {day.events.map(event => {
                        const style = getPositionStyle(dayIndex, event.timeStart, event.timeEnd);
                        const isTightWindow = event.label.includes('(Stretto!)');
                        {
                          /* External Events - Not draggable */
                        }
                        if (event.type === 'external') {
                          return <div key={event.id} className="absolute flex items-center justify-center bg-gray-100 border border-dashed border-gray-300 rounded-lg px-2 cursor-default z-1" style={{
                            left: '2px',
                            width: 'calc(100% - 4px)',
                            top: `calc(${style.top} + 2px)`,
                            height: `calc(${style.height} - 4px)`
                          }} title={`Evento da ${event.source === 'google' ? 'Google Calendar' : 'Outlook'}`} data-id="element-940">
                                    <div className="flex flex-col items-center min-w-0" data-id="element-941">
                                      <div className="flex items-center gap-1" data-id="element-942">
                                        {event.source === 'google' ? <img src="https://cdn.magicpatterns.com/uploads/kH8QC7bV2biHRqFW7LmspQ/Google_Calendar_icon.png" alt="Google Calendar" className="w-3 h-3" data-id="element-943" /> : <img src="https://cdn.magicpatterns.com/uploads/mD3xBbUgdyBxcWhKYbGEK6/Microsoft_Office_Outlook.png" alt="Outlook" className="w-3 h-3" data-id="element-944" />}
                                        <span className="text-[10px] text-gray-500 font-medium" data-id="element-945">
                                          {event.timeStart}
                                        </span>
                                      </div>
                                      <span className="text-[10px] text-gray-500 truncate" data-id="element-946">
                                        {event.label}
                                      </span>
                                    </div>
                                  </div>;
                        }
                        {
                          /* Check-in/Check-out Events - Fixed, not draggable */
                        }
                        if (event.type === 'checkout' || event.type === 'checkin') {
                          const isCheckout = event.type === 'checkout';
                          return <div key={event.id} onClick={() => handleEventClick(event)} className="absolute flex items-center justify-center bg-white/90 border border-gray-200 rounded-lg px-2 shadow-sm hover:shadow-md hover:border-gray-300 transition-all cursor-pointer z-5" style={{
                            left: '2px',
                            width: 'calc(100% - 4px)',
                            top: `calc(${style.top} + 2px)`,
                            height: `calc(${style.height} - 4px)`
                          }} title="Evento fisso da Vikey" data-id="element-947">
                                    <div className="flex flex-col items-center min-w-0" data-id="element-948">
                                      <div className="flex items-center gap-1" data-id="element-949">
                                        <Lock size={10} className="text-gray-400" data-id="element-950" />
                                        <span className="text-[13px] font-bold text-gray-800 leading-tight" data-id="element-951">
                                          {event.timeStart}
                                        </span>
                                      </div>
                                      <span className="text-xs text-gray-600 leading-tight truncate" data-id="element-952">
                                        {isCheckout ? 'Check-out' : 'Check-in'}
                                      </span>
                                    </div>
                                  </div>;
                        }
                        {
                          /* Cleaning Events - Draggable & Resizable */
                        }
                        if (event.type === 'cleaning-suggested' || event.type === 'cleaning-confirmed') {
                          const isConfirmed = event.type === 'cleaning-confirmed';
                          const displayLabel = isTightWindow ? 'Pulizia' : event.label;
                          const timeRange = event.timeEnd ? `${event.timeStart}-${event.timeEnd}` : event.timeStart;
                          return <div key={event.id} draggable onDragStart={e => handleDragStart(e, event.id, dayIndex)} className={`group absolute flex flex-col items-center justify-center rounded-lg px-2 transition-all duration-200 cursor-move z-5 draggable-event hover:shadow-md hover:scale-[1.02] ${draggedEvent === event.id ? 'opacity-50' : ''} ${isTightWindow ? 'bg-orange-50 border border-orange-300 hover:border-orange-400' : isConfirmed ? 'bg-green-50 border border-green-200 hover:border-green-300' : 'bg-teal-50 border border-cyan-200 hover:border-cyan-300'}`} style={{
                            left: '2px',
                            width: 'calc(100% - 4px)',
                            top: `calc(${style.top} + 2px)`,
                            height: `calc(${style.height} - 4px)`
                          }} data-id="element-953">
                                    {/* Drag Handle */}
                                    <div className="absolute left-1 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none" data-id="element-954">
                                      <GripVertical size={12} className={`${isTightWindow ? 'text-orange-400' : isConfirmed ? 'text-green-400' : 'text-cyan-400'}`} data-id="element-955" />
                                    </div>

                                    <div className="flex items-center gap-1 min-w-0" data-id="element-956">
                                      {isTightWindow && <AlertTriangle size={12} className="text-orange-500 flex-shrink-0" data-id="element-957" />}
                                      <span className={`text-[11px] font-bold leading-tight ${isTightWindow ? 'text-orange-700' : isConfirmed ? 'text-green-700' : 'text-cyan-700'}`} data-id="element-958">
                                        {timeRange}
                                      </span>
                                    </div>
                                    <span className={`text-[10px] leading-tight truncate ${isTightWindow ? 'text-orange-600' : isConfirmed ? 'text-green-600' : 'text-cyan-600'}`} data-id="element-959">
                                      {displayLabel}
                                    </span>
                                    {!isConfirmed && <button className={`mt-1 p-1 rounded-md transition-colors flex-shrink-0 ${isTightWindow ? 'bg-orange-100 hover:bg-orange-200 text-orange-600' : 'bg-teal-100 hover:bg-cyan-200 text-cyan-600'}`} onClick={e => e.stopPropagation()} data-id="element-960">
                                        <Check size={10} data-id="element-961" />
                                      </button>}

                                    {/* Resize Handle */}
                                    <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-8 h-3 opacity-0 group-hover:opacity-100 transition-opacity cursor-ns-resize flex justify-center items-center gap-0.5" onMouseDown={e => handleResizeStart(e, event.id)} data-id="element-962">
                                      <span className={`w-1 h-1 rounded-full ${isTightWindow ? 'bg-orange-400' : isConfirmed ? 'bg-green-400' : 'bg-cyan-400'}`} data-id="element-963"></span>
                                      <span className={`w-1 h-1 rounded-full ${isTightWindow ? 'bg-orange-400' : isConfirmed ? 'bg-green-400' : 'bg-cyan-400'}`} data-id="element-964"></span>
                                      <span className={`w-1 h-1 rounded-full ${isTightWindow ? 'bg-orange-400' : isConfirmed ? 'bg-green-400' : 'bg-cyan-400'}`} data-id="element-965"></span>
                                    </div>
                                  </div>;
                        }
                        return null;
                      })}
                          </div>)}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Gantt Footer - Updated Legend */}
              <div className="border-t border-gray-200 bg-gray-50/50 px-4 lg:px-6 py-4" data-id="element-966">
                <div className="flex flex-col gap-3" data-id="element-967">
                  <div className="flex flex-wrap items-center gap-3 lg:gap-6" data-id="element-968">
                    <span className="text-xs font-medium text-gray-500 uppercase tracking-wide" data-id="element-969">
                      Legenda:
                    </span>
                    <div className="flex items-center gap-2" data-id="element-970">
                      <div className="w-3 h-3 bg-white border border-gray-200 rounded flex items-center justify-center" data-id="element-971">
                        <Lock size={6} className="text-gray-400" data-id="element-972" />
                      </div>
                      <span className="text-xs text-gray-600" data-id="element-973">Check-out</span>
                    </div>
                    <div className="flex items-center gap-2" data-id="element-974">
                      <div className="w-3 h-3 bg-teal-50 border border-cyan-200 rounded" data-id="element-975"></div>
                      <span className="text-xs text-gray-600" data-id="element-976">
                        Pulizia suggerita
                      </span>
                    </div>
                    <div className="flex items-center gap-2" data-id="element-977">
                      <div className="w-3 h-3 bg-green-50 border border-green-200 rounded" data-id="element-978"></div>
                      <span className="text-xs text-gray-600" data-id="element-979">Confermata</span>
                    </div>
                    <div className="flex items-center gap-2" data-id="element-980">
                      <div className="w-3 h-3 bg-white border border-gray-200 rounded flex items-center justify-center" data-id="element-981">
                        <Lock size={6} className="text-gray-400" data-id="element-982" />
                      </div>
                      <span className="text-xs text-gray-600" data-id="element-983">Check-in</span>
                    </div>
                    <div className="flex items-center gap-2" data-id="element-984">
                      <div className="w-3 h-3 bg-orange-50 border border-orange-300 rounded" data-id="element-985"></div>
                      <span className="text-xs text-gray-600" data-id="element-986">Stretta</span>
                    </div>
                    <div className="flex items-center gap-2" data-id="element-987">
                      <div className="w-3 h-3 bg-gray-100 border border-dashed border-gray-300 rounded" data-id="element-988"></div>
                      <span className="text-xs text-gray-600" data-id="element-989">
                        Evento esterno
                      </span>
                    </div>
                  </div>

                  {/* Interaction hints */}
                  <div className="flex flex-wrap items-center gap-4 text-xs text-gray-400" data-id="element-990">
                    <span className="flex items-center gap-1" data-id="element-991">
                      <Lock size={10} data-id="element-992" /> = evento fisso
                    </span>
                    <span className="flex items-center gap-1" data-id="element-993">
                      <GripVertical size={10} data-id="element-994" /> = trascinabile
                    </span>
                    <span data-id="element-995">
                      Gli interventi si spostano a intervalli di 30 minuti
                    </span>
                  </div>
                </div>
              </div>

              {/* Conflict Warning - Show when conflict detected */}
              {showConflictWarning && <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-50 animate-in slide-in-from-bottom duration-300" data-id="element-996">
                  <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-center gap-2 text-red-700 text-sm shadow-lg" data-id="element-997">
                    <AlertTriangle size={16} data-id="element-998" />
                    <span data-id="element-999">{conflictMessage}</span>
                  </div>
                </div>}

              {/* Confirmation Toast - Show after successful drag/resize */}
              {showConfirmation && <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-50 animate-in slide-in-from-bottom duration-300" data-id="element-1000">
                  <div className="bg-white shadow-lg rounded-lg p-4 border border-gray-200 flex items-center justify-between gap-4" data-id="element-1001">
                    <span className="text-sm text-gray-700" data-id="element-1002">
                      {confirmationMessage}
                    </span>
                    <button onClick={() => setShowConfirmation(false)} className="text-sm text-teal-deep hover:text-teal-deep/80 font-medium" data-id="element-1003">
                      OK
                    </button>
                  </div>
                </div>}
            </div>}

          {/* List View - Mobile-friendly */}
          {viewMode === 'list' && <div className="bg-white rounded-xl border border-gray-200 p-4 lg:p-5" data-id="element-1004">
              <div className="space-y-6" data-id="element-1005">
                {/* Da confermare section */}
                <div data-id="element-1006">
                  <h3 className="text-xs text-gray-400 uppercase tracking-wider font-medium mb-4" data-id="element-1007">
                    Da confermare (10)
                  </h3>
                  <div className="space-y-4" data-id="element-1008">
                    {/* Intervention 1 - Lun 15 Gen */}
                    <div className="bg-white border border-gray-200 rounded-xl p-4" data-id="element-1009">
                      <div className="flex items-center justify-between mb-4" data-id="element-1010">
                        <div className="text-sm font-bold text-navy" data-id="element-1011">
                          Lunedì 15 Gennaio
                        </div>
                        <button className="px-4 py-2 bg-teal-deep text-white rounded-full text-sm font-medium hover:bg-[#50BFD9] flex items-center gap-1 min-h-[40px]" data-id="element-1012">
                          <Check size={14} data-id="element-1013" />
                          Conferma
                        </button>
                      </div>
                      <div className="space-y-1" data-id="element-1014">
                        <button onClick={() => handleEventClick(weekData[0].events[0])} className="inline-flex items-center gap-2 text-left bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 hover:border-gray-300 hover:bg-gray-100 transition-all cursor-pointer" data-id="element-1015">
                          <Lock size={12} className="text-gray-400 flex-shrink-0" data-id="element-1016" />
                          <span className="text-sm font-bold text-navy" data-id="element-1017">
                            10:00
                          </span>
                          <span className="text-sm text-gray-600" data-id="element-1018">
                            Check-out
                          </span>
                          <span className="text-sm text-gray-400" data-id="element-1019">
                            (Roberto F.)
                          </span>
                        </button>
                        <div className="flex items-center gap-2 ml-0.5 pl-px" data-id="element-1020">
                          <div className="w-px h-3 bg-gray-200" data-id="element-1021"></div>
                          <span className="text-xs text-gray-400" data-id="element-1022">
                            ↓ 1h di margine
                          </span>
                        </div>
                        <div className="flex items-center gap-2" data-id="element-1023">
                          <div className="w-1.5 h-1.5 rounded-full bg-teal-deep" data-id="element-1024"></div>
                          <span className="text-sm font-medium text-teal-deep" data-id="element-1025">
                            11:00 - 15:00
                          </span>
                          <span className="text-sm text-gray-600" data-id="element-1026">Pulizia</span>
                          <span className="text-xs text-gray-400" data-id="element-1027">(4h)</span>
                        </div>
                        <div className="flex items-center gap-2 ml-0.5 pl-px" data-id="element-1028">
                          <div className="w-px h-3 bg-gray-200" data-id="element-1029"></div>
                          <span className="text-xs text-orange-500" data-id="element-1030">
                            ↓ 0 margine
                          </span>
                        </div>
                        <button onClick={() => handleEventClick(weekData[0].events[2])} className="inline-flex items-center gap-2 text-left bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 hover:border-gray-300 hover:bg-gray-100 transition-all cursor-pointer" data-id="element-1031">
                          <Lock size={12} className="text-gray-400 flex-shrink-0" data-id="element-1032" />
                          <span className="text-sm font-bold text-navy" data-id="element-1033">
                            15:00
                          </span>
                          <span className="text-sm text-gray-600" data-id="element-1034">
                            Check-in
                          </span>
                          <span className="text-sm text-gray-400" data-id="element-1035">
                            (Francesca N.)
                          </span>
                        </button>
                      </div>
                      <div className="mt-4 pt-3 border-t border-gray-100" data-id="element-1036">
                        <div className="flex items-start gap-2" data-id="element-1037">
                          <span className="text-sm" data-id="element-1038">💡</span>
                          <p className="text-xs text-gray-500" data-id="element-1039">
                            <span className="font-medium text-gray-600" data-id="element-1040">
                              AI:
                            </span>{' '}
                            Inizio +1h per aerazione, durata 4h basata su 85m²
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Intervention 2 - Mar 16 Gen */}
                    <div className="bg-white border border-gray-200 rounded-xl p-4" data-id="element-1041">
                      <div className="flex items-center justify-between mb-4" data-id="element-1042">
                        <div className="text-sm font-bold text-navy" data-id="element-1043">
                          Martedì 16 Gennaio
                        </div>
                        <button className="px-4 py-2 bg-teal-deep text-white rounded-full text-sm font-medium hover:bg-[#50BFD9] flex items-center gap-1 min-h-[40px]" data-id="element-1044">
                          <Check size={14} data-id="element-1045" />
                          Conferma
                        </button>
                      </div>
                      <div className="space-y-1" data-id="element-1046">
                        <button onClick={() => handleEventClick(weekData[1].events[0])} className="inline-flex items-center gap-2 text-left bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 hover:border-gray-300 hover:bg-gray-100 transition-all cursor-pointer" data-id="element-1047">
                          <Lock size={12} className="text-gray-400 flex-shrink-0" data-id="element-1048" />
                          <span className="text-sm font-bold text-navy" data-id="element-1049">
                            11:00
                          </span>
                          <span className="text-sm text-gray-600" data-id="element-1050">
                            Check-out
                          </span>
                          <span className="text-sm text-gray-400" data-id="element-1051">
                            (Luca P.)
                          </span>
                        </button>
                        <div className="flex items-center gap-2 ml-0.5 pl-px" data-id="element-1052">
                          <div className="w-px h-3 bg-gray-200" data-id="element-1053"></div>
                          <span className="text-xs text-gray-400" data-id="element-1054">
                            ↓ 1h di margine
                          </span>
                        </div>
                        <div className="flex items-center gap-2" data-id="element-1055">
                          <div className="w-1.5 h-1.5 rounded-full bg-teal-deep" data-id="element-1056"></div>
                          <span className="text-sm font-medium text-teal-deep" data-id="element-1057">
                            12:00 - 16:00
                          </span>
                          <span className="text-sm text-gray-600" data-id="element-1058">Pulizia</span>
                          <span className="text-xs text-gray-400" data-id="element-1059">(4h)</span>
                        </div>
                        <div className="flex items-center gap-2 ml-0.5 pl-px" data-id="element-1060">
                          <div className="w-px h-3 bg-gray-200" data-id="element-1061"></div>
                          <span className="text-xs text-orange-500" data-id="element-1062">
                            ↓ 0 margine
                          </span>
                        </div>
                        <button onClick={() => handleEventClick(weekData[1].events[2])} className="inline-flex items-center gap-2 text-left bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 hover:border-gray-300 hover:bg-gray-100 transition-all cursor-pointer" data-id="element-1063">
                          <Lock size={12} className="text-gray-400 flex-shrink-0" data-id="element-1064" />
                          <span className="text-sm font-bold text-navy" data-id="element-1065">
                            16:00
                          </span>
                          <span className="text-sm text-gray-600" data-id="element-1066">
                            Check-in
                          </span>
                          <span className="text-sm text-gray-400" data-id="element-1067">
                            (Giulia S.)
                          </span>
                        </button>
                      </div>
                      <div className="mt-4 pt-3 border-t border-gray-100" data-id="element-1068">
                        <div className="flex items-start gap-2" data-id="element-1069">
                          <span className="text-sm" data-id="element-1070">💡</span>
                          <p className="text-xs text-gray-500" data-id="element-1071">
                            <span className="font-medium text-gray-600" data-id="element-1072">
                              AI:
                            </span>{' '}
                            Ampia finestra di 5h disponibile. Programmato inizio
                            alle 12:00 per ottimizzare i turni.
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Intervention 3 - Gio 18 Gen */}
                    <div className="bg-white border border-gray-200 rounded-xl p-4" data-id="element-1073">
                      <div className="flex items-center justify-between mb-4" data-id="element-1074">
                        <div className="text-sm font-bold text-navy" data-id="element-1075">
                          Giovedì 18 Gennaio
                        </div>
                        <button className="px-4 py-2 bg-teal-deep text-white rounded-full text-sm font-medium hover:bg-[#50BFD9] flex items-center gap-1 min-h-[40px]" data-id="element-1076">
                          <Check size={14} data-id="element-1077" />
                          Conferma
                        </button>
                      </div>
                      <div className="space-y-1" data-id="element-1078">
                        <button onClick={() => handleEventClick(weekData[3].events[0])} className="inline-flex items-center gap-2 text-left bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 hover:border-gray-300 hover:bg-gray-100 transition-all cursor-pointer" data-id="element-1079">
                          <Lock size={12} className="text-gray-400 flex-shrink-0" data-id="element-1080" />
                          <span className="text-sm font-bold text-navy" data-id="element-1081">
                            10:00
                          </span>
                          <span className="text-sm text-gray-600" data-id="element-1082">
                            Check-out
                          </span>
                          <span className="text-sm text-gray-400" data-id="element-1083">
                            (Francesca N.)
                          </span>
                        </button>
                        <div className="flex items-center gap-2 ml-0.5 pl-px" data-id="element-1084">
                          <div className="w-px h-3 bg-gray-200" data-id="element-1085"></div>
                          <span className="text-xs text-gray-400" data-id="element-1086">
                            ↓ 1h di margine
                          </span>
                        </div>
                        <div className="flex items-center gap-2" data-id="element-1087">
                          <div className="w-1.5 h-1.5 rounded-full bg-teal-deep" data-id="element-1088"></div>
                          <span className="text-sm font-medium text-teal-deep" data-id="element-1089">
                            11:00 - 14:00
                          </span>
                          <span className="text-sm text-gray-600" data-id="element-1090">Pulizia</span>
                          <span className="text-xs text-gray-400" data-id="element-1091">(3h)</span>
                        </div>
                        <div className="flex items-center gap-2 ml-0.5 pl-px" data-id="element-1092">
                          <div className="w-px h-3 bg-gray-200" data-id="element-1093"></div>
                          <span className="text-xs text-orange-500" data-id="element-1094">
                            ↓ 0 margine
                          </span>
                        </div>
                        <button onClick={() => handleEventClick(weekData[3].events[2])} className="inline-flex items-center gap-2 text-left bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 hover:border-gray-300 hover:bg-gray-100 transition-all cursor-pointer" data-id="element-1095">
                          <Lock size={12} className="text-gray-400 flex-shrink-0" data-id="element-1096" />
                          <span className="text-sm font-bold text-navy" data-id="element-1097">
                            14:00
                          </span>
                          <span className="text-sm text-gray-600" data-id="element-1098">
                            Check-in
                          </span>
                          <span className="text-sm text-gray-400" data-id="element-1099">
                            (Davide C.)
                          </span>
                        </button>
                      </div>
                      <div className="mt-4 pt-3 border-t border-gray-100" data-id="element-1100">
                        <div className="flex items-start gap-2" data-id="element-1101">
                          <span className="text-sm" data-id="element-1102">💡</span>
                          <p className="text-xs text-gray-500" data-id="element-1103">
                            <span className="font-medium text-gray-600" data-id="element-1104">
                              AI:
                            </span>{' '}
                            Durata 3h ottimizzata per metratura standard.
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Intervention 4 - Ven 19 Gen (Tight window) */}
                    <div className="bg-white border border-gray-200 rounded-xl p-4 border-l-2 border-l-orange-400" data-id="element-1105">
                      <div className="flex items-center justify-between mb-4" data-id="element-1106">
                        <div className="flex items-center gap-2" data-id="element-1107">
                          <div className="text-sm font-bold text-navy" data-id="element-1108">
                            Venerdì 19 Gennaio
                          </div>
                          <span className="inline-flex items-center gap-1 text-xs text-orange-600 bg-orange-50 px-2 py-0.5 rounded-full font-medium" data-id="element-1109">
                            <AlertTriangle size={12} data-id="element-1110" />
                            Finestra stretta
                          </span>
                        </div>
                        <button className="px-4 py-2 bg-teal-deep text-white rounded-full text-sm font-medium hover:bg-[#50BFD9] flex items-center gap-1 min-h-[40px]" data-id="element-1111">
                          <Check size={14} data-id="element-1112" />
                          Conferma
                        </button>
                      </div>
                      <div className="space-y-1" data-id="element-1113">
                        <button onClick={() => handleEventClick(weekData[4].events[0])} className="inline-flex items-center gap-2 text-left bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 hover:border-gray-300 hover:bg-gray-100 transition-all cursor-pointer" data-id="element-1114">
                          <Lock size={12} className="text-gray-400 flex-shrink-0" data-id="element-1115" />
                          <span className="text-sm font-bold text-navy" data-id="element-1116">
                            11:00
                          </span>
                          <span className="text-sm text-gray-600" data-id="element-1117">
                            Check-out
                          </span>
                          <span className="text-sm text-gray-400" data-id="element-1118">
                            (Sofia L.)
                          </span>
                        </button>
                        <div className="flex items-center gap-2 ml-0.5 pl-px" data-id="element-1119">
                          <div className="w-px h-3 bg-gray-200" data-id="element-1120"></div>
                          <span className="text-xs text-gray-400" data-id="element-1121">
                            ↓ 1h di margine
                          </span>
                        </div>
                        <div className="flex items-center gap-2" data-id="element-1122">
                          <div className="w-1.5 h-1.5 rounded-full bg-teal-deep" data-id="element-1123"></div>
                          <span className="text-sm font-medium text-teal-deep" data-id="element-1124">
                            12:00 - 14:30
                          </span>
                          <span className="text-sm text-gray-600" data-id="element-1125">Pulizia</span>
                          <span className="text-xs text-gray-400" data-id="element-1126">
                            (2h 30min)
                          </span>
                        </div>
                        <div className="flex items-center gap-2 ml-0.5 pl-px" data-id="element-1127">
                          <div className="w-px h-3 bg-gray-200" data-id="element-1128"></div>
                          <span className="text-xs text-orange-500" data-id="element-1129">
                            ↓ 0 margine
                          </span>
                        </div>
                        <button onClick={() => handleEventClick(weekData[4].events[2])} className="inline-flex items-center gap-2 text-left bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 hover:border-gray-300 hover:bg-gray-100 transition-all cursor-pointer" data-id="element-1130">
                          <Lock size={12} className="text-gray-400 flex-shrink-0" data-id="element-1131" />
                          <span className="text-sm font-bold text-navy" data-id="element-1132">
                            14:30
                          </span>
                          <span className="text-sm text-gray-600" data-id="element-1133">
                            Check-in
                          </span>
                          <span className="text-sm text-gray-400" data-id="element-1134">
                            (Paolo V.)
                          </span>
                        </button>
                      </div>
                      <div className="mt-4 pt-3 border-t border-gray-100" data-id="element-1135">
                        <div className="flex items-start gap-2" data-id="element-1136">
                          <span className="text-sm" data-id="element-1137">💡</span>
                          <p className="text-xs text-orange-600" data-id="element-1138">
                            <span className="font-medium text-orange-700" data-id="element-1139">
                              AI:
                            </span>{' '}
                            ⚠️ Finestra stretta: solo 2h 30min disponibili.
                            Consigliato team di 2 persone per completare in
                            tempo.
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Intervention - Sab 20 Gen */}
                    <div className="bg-white border border-gray-200 rounded-xl p-4" data-id="element-1140">
                      {/* Date header with button */}
                      <div className="flex items-center justify-between mb-4" data-id="element-1141">
                        <div className="text-sm font-bold text-navy" data-id="element-1142">
                          Sabato 20 Gennaio
                        </div>
                        <button className="px-4 py-2 bg-teal-deep text-white rounded-full text-sm font-medium hover:bg-[#50BFD9] flex items-center gap-1 min-h-[40px]" data-id="element-1143">
                          <Check size={14} data-id="element-1144" />
                          Conferma
                        </button>
                      </div>

                      {/* Timeline */}
                      <div className="space-y-1" data-id="element-1145">
                        {/* Checkout row - clickable button */}
                        <button onClick={() => handleEventClick(weekData[5].events[0])} className="inline-flex items-center gap-2 text-left bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 hover:border-gray-300 hover:bg-gray-100 transition-all cursor-pointer" data-id="element-1146">
                          <Lock size={12} className="text-gray-400 flex-shrink-0" data-id="element-1147" />
                          <span className="text-sm font-bold text-navy" data-id="element-1148">
                            10:00
                          </span>
                          <span className="text-sm text-gray-600" data-id="element-1149">
                            Check-out
                          </span>
                          <span className="text-sm text-gray-400" data-id="element-1150">
                            (Anna B.)
                          </span>
                        </button>

                        {/* Margin indicator */}
                        <div className="flex items-center gap-2 ml-0.5 pl-px" data-id="element-1151">
                          <div className="w-px h-3 bg-gray-200" data-id="element-1152"></div>
                          <span className="text-xs text-gray-400" data-id="element-1153">
                            ↓ 1h di margine
                          </span>
                        </div>

                        {/* Cleaning row - no button */}
                        <div className="flex items-center gap-2" data-id="element-1154">
                          <div className="w-1.5 h-1.5 rounded-full bg-teal-deep" data-id="element-1155"></div>
                          <span className="text-sm font-medium text-teal-deep" data-id="element-1156">
                            11:00 - 16:00
                          </span>
                          <span className="text-sm text-gray-600" data-id="element-1157">Pulizia</span>
                          <span className="text-xs text-gray-400" data-id="element-1158">(5h)</span>
                        </div>

                        {/* Margin indicator */}
                        <div className="flex items-center gap-2 ml-0.5 pl-px" data-id="element-1159">
                          <div className="w-px h-3 bg-gray-200" data-id="element-1160"></div>
                          <span className="text-xs text-orange-500" data-id="element-1161">
                            ↓ 0 margine
                          </span>
                        </div>

                        {/* Checkin row - clickable button */}
                        <button onClick={() => handleEventClick(weekData[5].events[2])} className="inline-flex items-center gap-2 text-left bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 hover:border-gray-300 hover:bg-gray-100 transition-all cursor-pointer" data-id="element-1162">
                          <Lock size={12} className="text-gray-400 flex-shrink-0" data-id="element-1163" />
                          <span className="text-sm font-bold text-navy" data-id="element-1164">
                            16:00
                          </span>
                          <span className="text-sm text-gray-600" data-id="element-1165">
                            Check-in
                          </span>
                          <span className="text-sm text-gray-400" data-id="element-1166">
                            (Elena T.)
                          </span>
                        </button>
                      </div>

                      {/* AI Suggestion */}
                      <div className="mt-4 pt-3 border-t border-gray-100" data-id="element-1167">
                        <div className="flex items-start gap-2" data-id="element-1168">
                          <span className="text-sm" data-id="element-1169">💡</span>
                          <p className="text-xs text-gray-500" data-id="element-1170">
                            <span className="font-medium text-gray-600" data-id="element-1171">
                              AI:
                            </span>{' '}
                            Ampia finestra di 5h disponibile. Programmato inizio
                            alle 11:00 per ottimizzare i turni del fornitore.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Confermate section */}
                <div data-id="element-1172">
                  <h3 className="text-xs text-gray-400 uppercase tracking-wider font-medium mb-4" data-id="element-1173">
                    Confermate
                  </h3>
                  <div className="text-center py-8" data-id="element-1174">
                    <p className="text-sm text-gray-400" data-id="element-1175">
                      Nessun intervento confermato in questo periodo
                    </p>
                  </div>
                </div>
              </div>
            </div>}
        </div>
      </div>

      {/* Google Calendar Modal */}
      {isGoogleModalOpen && <div className="fixed inset-0 bg-black/30 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-in fade-in duration-200" onClick={() => setIsGoogleModalOpen(false)} data-id="element-1176">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col animate-in zoom-in-95 duration-300" onClick={e => e.stopPropagation()} data-id="element-1177">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-4 lg:p-6 border-b border-gray-200" data-id="element-1178">
              <div className="flex items-center gap-3" data-id="element-1179">
                <img src="https://cdn.magicpatterns.com/uploads/kH8QC7bV2biHRqFW7LmspQ/Google_Calendar_icon.png" alt="Google Calendar" className="w-8 h-8" data-id="element-1180" />
                <div data-id="element-1181">
                  <h2 className="text-lg lg:text-xl font-bold text-navy" data-id="element-1182">
                    Google Calendar
                  </h2>
                  <p className="text-xs lg:text-sm text-gray-500 mt-0.5" data-id="element-1183">
                    Gestisci account e calendari Google
                  </p>
                </div>
              </div>
              <button onClick={() => setIsGoogleModalOpen(false)} className="p-2 hover:bg-gray-100 rounded-lg transition-colors min-h-[44px] min-w-[44px] flex items-center justify-center" data-id="element-1184">
                <X size={20} className="text-gray-500" data-id="element-1185" />
              </button>
            </div>

            {/* Modal Content */}
            <div className="flex-1 overflow-y-auto p-4 lg:p-6" data-id="element-1186">
              <div className="space-y-6" data-id="element-1187">
                {/* Connected Accounts */}
                {googleAccounts.length > 0 ? <div className="space-y-4" data-id="element-1188">
                    {googleAccounts.map(account => <div key={account.id} className="border border-gray-200 rounded-lg p-4" data-id="element-1189">
                        <div className="flex items-center justify-between mb-4" data-id="element-1190">
                          <div className="flex items-center gap-2" data-id="element-1191">
                            <User size={16} className="text-gray-400" data-id="element-1192" />
                            <span className="text-sm font-semibold text-navy" data-id="element-1193">
                              {account.email}
                            </span>
                          </div>
                          <button className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-md transition-colors" data-id="element-1194">
                            <X size={16} data-id="element-1195" />
                          </button>
                        </div>

                        {/* Calendars List with Checkboxes */}
                        <div className="space-y-2" data-id="element-1196">
                          <p className="text-xs text-gray-500 font-medium mb-2" data-id="element-1197">
                            Seleziona calendari da sincronizzare:
                          </p>
                          {account.calendars.map(calendar => <label key={calendar.id} className="flex items-center gap-3 p-2 hover:bg-gray-50 rounded-lg cursor-pointer transition-colors" data-id="element-1198">
                              <input type="checkbox" checked={calendar.selected} onChange={() => toggleGoogleCalendar(account.id, calendar.id)} className="w-4 h-4 text-teal-deep border-gray-300 rounded focus:ring-teal-deep focus:ring-2" data-id="element-1199" />
                              <span className="text-sm text-gray-700" data-id="element-1200">
                                {calendar.name}
                              </span>
                            </label>)}
                        </div>
                      </div>)}
                  </div> : <div className="text-center py-8 text-sm text-gray-500" data-id="element-1201">
                    Nessun account Google connesso
                  </div>}

                {/* Add Account Button */}
                <button className="w-full p-4 border-2 border-dashed border-gray-300 rounded-lg hover:border-teal-deep hover:bg-teal-50/30 transition-all flex items-center justify-center gap-2 text-gray-600 hover:text-teal-deep min-h-[44px]" data-id="element-1202">
                  <Plus size={18} data-id="element-1203" />
                  <span className="text-sm font-medium" data-id="element-1204">
                    Aggiungi altro account Google
                  </span>
                </button>

                {/* Info Banner */}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-start gap-3" data-id="element-1205">
                  <CalendarIcon size={18} className="text-blue-600 flex-shrink-0 mt-0.5" data-id="element-1206" />
                  <div data-id="element-1207">
                    <p className="text-sm font-medium text-blue-900 mb-1" data-id="element-1208">
                      Sincronizzazione automatica
                    </p>
                    <p className="text-xs text-blue-700" data-id="element-1209">
                      Solo i calendari selezionati verranno sincronizzati con
                      CleanAi per evitare sovrapposizioni negli interventi.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="border-t border-gray-200 p-4 lg:p-6" data-id="element-1210">
              <button onClick={() => setIsGoogleModalOpen(false)} className="w-full px-4 py-2.5 bg-teal-deep text-white rounded-full hover:bg-[#50BFD9] transition-colors font-medium text-sm min-h-[44px]" data-id="element-1211">
                Salva e Chiudi
              </button>
            </div>
          </div>
        </div>}

      {/* Outlook Calendar Modal */}
      {isOutlookModalOpen && <div className="fixed inset-0 bg-black/30 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-in fade-in duration-200" onClick={() => setIsOutlookModalOpen(false)} data-id="element-1212">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col animate-in zoom-in-95 duration-300" onClick={e => e.stopPropagation()} data-id="element-1213">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-4 lg:p-6 border-b border-gray-200" data-id="element-1214">
              <div className="flex items-center gap-3" data-id="element-1215">
                <img src="https://cdn.magicpatterns.com/uploads/mD3xBbUgdyBxcWhKYbGEK6/Microsoft_Office_Outlook.png" alt="Outlook" className="w-8 h-8" data-id="element-1216" />
                <div data-id="element-1217">
                  <h2 className="text-lg lg:text-xl font-bold text-navy" data-id="element-1218">
                    Outlook Calendar
                  </h2>
                  <p className="text-xs lg:text-sm text-gray-500 mt-0.5" data-id="element-1219">
                    Gestisci account e calendari Outlook
                  </p>
                </div>
              </div>
              <button onClick={() => setIsOutlookModalOpen(false)} className="p-2 hover:bg-gray-100 rounded-lg transition-colors min-h-[44px] min-w-[44px] flex items-center justify-center" data-id="element-1220">
                <X size={20} className="text-gray-500" data-id="element-1221" />
              </button>
            </div>

            {/* Modal Content */}
            <div className="flex-1 overflow-y-auto p-4 lg:p-6" data-id="element-1222">
              <div className="space-y-6" data-id="element-1223">
                {/* Connected Accounts */}
                {integrations.outlook && outlookAccounts.length > 0 ? <div className="space-y-4" data-id="element-1224">
                    {outlookAccounts.map(account => <div key={account.id} className="border border-gray-200 rounded-lg p-4" data-id="element-1225">
                        <div className="flex items-center justify-between mb-4" data-id="element-1226">
                          <div className="flex items-center gap-2" data-id="element-1227">
                            <User size={16} className="text-gray-400" data-id="element-1228" />
                            <span className="text-sm font-semibold text-navy" data-id="element-1229">
                              {account.email}
                            </span>
                          </div>
                          <button className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-md transition-colors" data-id="element-1230">
                            <X size={16} data-id="element-1231" />
                          </button>
                        </div>

                        {/* Calendars List with Checkboxes */}
                        <div className="space-y-2" data-id="element-1232">
                          <p className="text-xs text-gray-500 font-medium mb-2" data-id="element-1233">
                            Seleziona calendari da sincronizzare:
                          </p>
                          {account.calendars.map(calendar => <label key={calendar.id} className="flex items-center gap-3 p-2 hover:bg-gray-50 rounded-lg cursor-pointer transition-colors" data-id="element-1234">
                              <input type="checkbox" checked={calendar.selected} onChange={() => toggleOutlookCalendar(account.id, calendar.id)} className="w-4 h-4 text-teal-deep border-gray-300 rounded focus:ring-teal-deep focus:ring-2" data-id="element-1235" />
                              <span className="text-sm text-gray-700" data-id="element-1236">
                                {calendar.name}
                              </span>
                            </label>)}
                        </div>
                      </div>)}
                  </div> : <div className="text-center py-8" data-id="element-1237">
                    <p className="text-sm text-gray-500 mb-4" data-id="element-1238">
                      Nessun account Outlook connesso
                    </p>
                    <button className="px-4 py-2.5 bg-teal-deep text-white rounded-lg hover:bg-teal-deep/90 transition-colors font-medium text-sm min-h-[44px]" data-id="element-1239">
                      Connetti Outlook
                    </button>
                  </div>}

                {/* Add Account Button - Only show if already connected */}
                {integrations.outlook && <button className="w-full p-4 border-2 border-dashed border-gray-300 rounded-lg hover:border-teal-deep hover:bg-teal-50/30 transition-all flex items-center justify-center gap-2 text-gray-600 hover:text-teal-deep min-h-[44px]" data-id="element-1240">
                    <Plus size={18} data-id="element-1241" />
                    <span className="text-sm font-medium" data-id="element-1242">
                      Aggiungi altro account Outlook
                    </span>
                  </button>}

                {/* Info Banner */}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-start gap-3" data-id="element-1243">
                  <CalendarIcon size={18} className="text-blue-600 flex-shrink-0 mt-0.5" data-id="element-1244" />
                  <div data-id="element-1245">
                    <p className="text-sm font-medium text-blue-900 mb-1" data-id="element-1246">
                      Sincronizzazione automatica
                    </p>
                    <p className="text-xs text-blue-700" data-id="element-1247">
                      Solo i calendari selezionati verranno sincronizzati con
                      CleanAi per evitare sovrapposizioni negli interventi.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="border-t border-gray-200 p-4 lg:p-6" data-id="element-1248">
              <button onClick={() => setIsOutlookModalOpen(false)} className="w-full px-4 py-2.5 bg-teal-deep text-white rounded-full hover:bg-[#50BFD9] transition-colors font-medium text-sm min-h-[44px]" data-id="element-1249">
                {integrations.outlook ? 'Salva e Chiudi' : 'Chiudi'}
              </button>
            </div>
          </div>
        </div>}

      {/* Booking Details Modal - Full screen on mobile */}
      {isBookingModalOpen && selectedBooking && <div className="fixed inset-0 bg-black/30 backdrop-blur-sm flex items-center justify-center z-50 p-0 lg:p-4 animate-in fade-in duration-200" onClick={() => setIsBookingModalOpen(false)} data-id="element-1250">
          <div className="bg-white lg:rounded-xl shadow-2xl w-full h-full lg:h-[90vh] lg:max-w-5xl flex flex-col animate-in zoom-in-95 duration-300" onClick={e => e.stopPropagation()} data-id="element-1251">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-4 lg:p-6 border-b border-gray-200" data-id="element-1252">
              <div data-id="element-1253">
                <h2 className="text-lg lg:text-xl font-bold text-navy" data-id="element-1254">
                  Dettagli Prenotazione
                </h2>
                <p className="text-xs lg:text-sm text-gray-500 mt-1" data-id="element-1255">
                  ID: #VK-2024-001234
                </p>
              </div>
              <button onClick={() => setIsBookingModalOpen(false)} className="p-2 hover:bg-gray-100 rounded-lg transition-colors min-h-[44px] min-w-[44px] flex items-center justify-center" data-id="element-1256">
                <X size={20} className="text-gray-500" data-id="element-1257" />
              </button>
            </div>

            {/* Modal Content */}
            <div className="flex-1 overflow-y-auto p-4 lg:p-6" data-id="element-1258">
              <div className="max-w-4xl mx-auto space-y-4 lg:space-y-6" data-id="element-1259">
                {/* Booking Status Banner */}
                <div className="bg-green-50 border border-green-200 rounded-lg p-3 lg:p-4 flex items-center gap-3" data-id="element-1260">
                  <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0" data-id="element-1261">
                    <CalendarIcon className="text-green-600" size={20} data-id="element-1262" />
                  </div>
                  <div data-id="element-1263">
                    <p className="font-semibold text-green-900 text-sm lg:text-base" data-id="element-1264">
                      Prenotazione Confermata
                    </p>
                    <p className="text-xs lg:text-sm text-green-700" data-id="element-1265">
                      {selectedBooking.type === 'checkin' ? 'Check-in' : 'Check-out'}
                      : {selectedBooking.timeStart}
                    </p>
                  </div>
                </div>

                {/* Guest Information */}
                <div className="bg-white border border-gray-200 rounded-xl p-4 lg:p-6" data-id="element-1266">
                  <h3 className="text-base lg:text-lg font-bold text-navy mb-3 lg:mb-4 flex items-center gap-2" data-id="element-1267">
                    <User size={18} data-id="element-1268" />
                    Informazioni Ospite
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 lg:gap-6" data-id="element-1269">
                    <div data-id="element-1270">
                      <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-2" data-id="element-1271">
                        Nome Completo
                      </label>
                      <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg" data-id="element-1272">
                        <User size={16} className="text-gray-400" data-id="element-1273" />
                        <span className="text-navy font-medium text-sm" data-id="element-1274">
                          {selectedBooking.label}
                        </span>
                      </div>
                    </div>
                    <div data-id="element-1275">
                      <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-2" data-id="element-1276">
                        Email
                      </label>
                      <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg" data-id="element-1277">
                        <Mail size={16} className="text-gray-400" data-id="element-1278" />
                        <span className="text-navy text-sm break-all" data-id="element-1279">
                          ospite@email.com
                        </span>
                      </div>
                    </div>
                    <div data-id="element-1280">
                      <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-2" data-id="element-1281">
                        Telefono
                      </label>
                      <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg" data-id="element-1282">
                        <Phone size={16} className="text-gray-400" data-id="element-1283" />
                        <span className="text-navy text-sm" data-id="element-1284">
                          +39 348 123 4567
                        </span>
                      </div>
                    </div>
                    <div data-id="element-1285">
                      <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-2" data-id="element-1286">
                        Numero Ospiti
                      </label>
                      <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg" data-id="element-1287">
                        <Users size={16} className="text-gray-400" data-id="element-1288" />
                        <span className="text-navy text-sm" data-id="element-1289">2 adulti</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Property & Dates */}
                <div className="bg-white border border-gray-200 rounded-xl p-4 lg:p-6" data-id="element-1290">
                  <h3 className="text-base lg:text-lg font-bold text-navy mb-3 lg:mb-4 flex items-center gap-2" data-id="element-1291">
                    <Home size={18} data-id="element-1292" />
                    Dettagli Soggiorno
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 lg:gap-6" data-id="element-1293">
                    <div data-id="element-1294">
                      <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-2" data-id="element-1295">
                        Proprietà
                      </label>
                      <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg" data-id="element-1296">
                        <MapPin size={16} className="text-gray-400 flex-shrink-0" data-id="element-1297" />
                        <div data-id="element-1298">
                          <p className="text-navy font-medium text-sm" data-id="element-1299">
                            Villa Sunset
                          </p>
                          <p className="text-xs text-gray-500" data-id="element-1300">
                            Via dei Giardini 12, Milano
                          </p>
                        </div>
                      </div>
                    </div>
                    <div data-id="element-1301">
                      <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-2" data-id="element-1302">
                        Periodo
                      </label>
                      <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg" data-id="element-1303">
                        <CalendarIcon size={16} className="text-gray-400 flex-shrink-0" data-id="element-1304" />
                        <div data-id="element-1305">
                          <p className="text-navy font-medium text-sm" data-id="element-1306">
                            18 Gen - 22 Gen 2024
                          </p>
                          <p className="text-xs text-gray-500" data-id="element-1307">4 notti</p>
                        </div>
                      </div>
                    </div>
                    <div data-id="element-1308">
                      <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-2" data-id="element-1309">
                        Check-in
                      </label>
                      <div className="p-3 bg-teal-50 rounded-lg border border-cyan-200" data-id="element-1310">
                        <p className="text-teal-deep font-semibold text-sm" data-id="element-1311">
                          {selectedBooking.type === 'checkin' ? selectedBooking.timeStart : '14:00'}
                        </p>
                      </div>
                    </div>
                    <div data-id="element-1312">
                      <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-2" data-id="element-1313">
                        Check-out
                      </label>
                      <div className="p-3 bg-gray-50 rounded-lg" data-id="element-1314">
                        <p className="text-navy font-semibold text-sm" data-id="element-1315">
                          {selectedBooking.type === 'checkout' ? selectedBooking.timeStart : '11:00'}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Payment Information */}
                <div className="bg-white border border-gray-200 rounded-xl p-4 lg:p-6" data-id="element-1316">
                  <h3 className="text-base lg:text-lg font-bold text-navy mb-3 lg:mb-4 flex items-center gap-2" data-id="element-1317">
                    <CreditCard size={18} data-id="element-1318" />
                    Informazioni Pagamento
                  </h3>
                  <div className="space-y-3 lg:space-y-4" data-id="element-1319">
                    <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg" data-id="element-1320">
                      <span className="text-gray-600 text-sm" data-id="element-1321">
                        Tariffa notte (x4)
                      </span>
                      <span className="text-navy font-medium text-sm" data-id="element-1322">
                        €800,00
                      </span>
                    </div>
                    <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg" data-id="element-1323">
                      <span className="text-gray-600 text-sm" data-id="element-1324">
                        Pulizie finali
                      </span>
                      <span className="text-navy font-medium text-sm" data-id="element-1325">
                        €80,00
                      </span>
                    </div>
                    <div className="flex justify-between items-center p-3 lg:p-4 bg-teal-50 rounded-lg border-2 border-cyan-200" data-id="element-1326">
                      <span className="text-teal-deep font-bold text-sm lg:text-base" data-id="element-1327">
                        Totale Pagato
                      </span>
                      <span className="text-teal-deep font-bold text-lg lg:text-xl" data-id="element-1328">
                        €880,00
                      </span>
                    </div>
                    <div className="flex items-center gap-2 text-xs lg:text-sm text-green-700 bg-green-50 p-3 rounded-lg" data-id="element-1329">
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100" data-id="element-1330">
                        PAGATO
                      </span>
                      <span data-id="element-1331">Pagamento ricevuto il 10 Gen 2024</span>
                    </div>
                  </div>
                </div>

                {/* Integration Badge */}
                <div className="flex items-center justify-center gap-2 text-xs text-gray-400 italic py-4" data-id="element-1332">
                  <ExternalLink size={14} data-id="element-1333" />
                  <span data-id="element-1334">Dati sincronizzati con Vickey</span>
                </div>
              </div>
            </div>
          </div>
        </div>}
    </MobileLayout>;
}